import React from 'react';
import { FiPackage } from 'react-icons/fi';

// AppTilePicker — visual app picker used by the Send Commands modal.
//
// Replaces the small dropdown with a grid of clickable tiles, one per
// app. Each tile shows the app's icon (if known), its name, and an
// optional subtitle (version / package). Selected tile picks up a
// brand-orange ring; clicking the same tile again deselects it.
//
// Items shape:
//   { value, label, iconUrl?, subtitle?, disabled? }
//
// `value` is what the parent stores in state and uses to build the
// command string — the picker is presentational, so any caller can
// adapt their data (commonApps, app store, device app_list) to this
// shape and reuse the same UI.

export default function AppTilePicker({ items, value, onSelect, emptyText = 'No apps available' }) {
    if (!items || items.length === 0) {
        return (
            <p className="text-sm text-gray-500 px-1">{emptyText}</p>
        );
    }

    return (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2">
            {items.map((item) => {
                const isSelected = value === item.value;
                return (
                    <button
                        key={item.value}
                        type="button"
                        disabled={item.disabled}
                        onClick={() => onSelect(isSelected ? '' : item.value)}
                        className={`group flex flex-col items-center gap-2 p-3 rounded-lg border transition-colors text-left ${
                            isSelected
                                ? 'bg-brand/10 border-brand/40 ring-1 ring-brand/40'
                                : 'bg-white/[0.03] border-white/[0.08] hover:bg-white/[0.06] hover:border-white/[0.15]'
                        } ${item.disabled ? 'opacity-40 cursor-not-allowed' : ''}`}
                        title={item.label}
                    >
                        <div className="w-12 h-12 rounded-lg bg-white/[0.04] flex items-center justify-center overflow-hidden shrink-0">
                            {item.iconUrl ? (
                                <img
                                    src={item.iconUrl}
                                    alt=""
                                    className="w-full h-full object-contain"
                                    onError={(e) => {
                                        // Fallback to the package icon if the
                                        // Wasabi-hosted icon 404s — keeps the
                                        // tile visually consistent.
                                        e.currentTarget.style.display = 'none';
                                    }}
                                />
                            ) : (
                                <FiPackage className="text-gray-500 text-xl" />
                            )}
                        </div>
                        <div className="text-xs font-medium text-white text-center truncate w-full">
                            {item.label}
                        </div>
                        {item.subtitle && (
                            <div className="text-[10px] text-gray-500 text-center truncate w-full">
                                {item.subtitle}
                            </div>
                        )}
                    </button>
                );
            })}
        </div>
    );
}
